import React from 'react';

/**
 * StickerText — renders text with a bold black outline and a soft
 * transparent-white outer outline, giving it a "sticker" look.
 *
 * Usage as a component:
 *   <StickerText className="text-lg font-black" whiteOpacity={0.35}>
 *     Hello world
 *   </StickerText>
 *
 * Usage as a className helper:
 *   import { stickerTextStyle } from './StickerText';
 *   <span style={stickerTextStyle(0.35)}>Hello world</span>
 */

export interface StickerTextProps extends React.HTMLAttributes<HTMLSpanElement> {
    /** Opacity of the white outer outline. Defaults to 0.35 (uses CSS var if not set). */
    whiteOpacity?: number;
    /** Tag to render as. Defaults to "span". */
    as?: React.ElementType;
    children: React.ReactNode;
}

/** Returns an inline style object that gives text the sticker outline effect.
 *  - Black outline: tight 4-direction offset shadows (sharp edge)
 *  - White glow: centered Gaussian blur — opacity-sensitive "backlit" halo
 */
const getBrightness = (color: string): number => {
    let r = 255, g = 255, b = 255;
    try {
        const cleanColor = color.trim().toLowerCase();
        if (cleanColor.startsWith('#')) {
            let hex = cleanColor.replace('#', '');
            if (hex.length === 3) {
                hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
            }
            r = parseInt(hex.substring(0, 2), 16);
            g = parseInt(hex.substring(2, 4), 16);
            b = parseInt(hex.substring(4, 6), 16);
        } else if (cleanColor.startsWith('rgb')) {
            const matches = cleanColor.match(/\d+/g);
            if (matches && matches.length >= 3) {
                r = parseInt(matches[0]);
                g = parseInt(matches[1]);
                b = parseInt(matches[2]);
            }
        } else if (cleanColor === 'black') {
            r = 0; g = 0; b = 0;
        }
    } catch (e) {}
    return Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));
};

export const stickerTextStyle = (whiteOpacity: number = 0.35, textColor?: string): React.CSSProperties => {
    const bk = '0.8px'; // outline offset
    
    let isDarkText = false;
    if (textColor) {
        isDarkText = getBrightness(textColor) < 127.5;
    }

    const outlineColor = isDarkText ? '#fff' : '#000';
    const glowColor = isDarkText ? `rgba(0,0,0,${whiteOpacity})` : `rgba(255,255,255,${whiteOpacity})`;

    // Sharp outline in 4 diagonal directions
    const sharpOutline = [
        `-${bk} -${bk} 0 ${outlineColor}`,
        ` ${bk} -${bk} 0 ${outlineColor}`,
        `-${bk}  ${bk} 0 ${outlineColor}`,
        ` ${bk}  ${bk} 0 ${outlineColor}`,
    ].join(', ');

    // Centered glow
    const glowRadius = '3px';
    const outerGlow = `0 0 ${glowRadius} ${glowColor}`;

    return {
        textShadow: [sharpOutline, outerGlow].join(', '),
    };
};

const StickerText: React.FC<StickerTextProps> = ({
    whiteOpacity,
    as: Tag = 'span',
    style,
    children,
    ...rest
}) => {
    const opacity = whiteOpacity ?? 0.35;
    return (
        <Tag
            {...rest}
            style={{ ...stickerTextStyle(opacity), ...style }}
        >
            {children}
        </Tag>
    );
};

export default StickerText;
